import React, { useState, useEffect, useRef } from 'react';
import './RegistrationScreen.css';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { db, storage } from './firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';


/* ─────────────────────────────────────────────
   Utility: Generate a unique Agent UID
   Format: LMT-XXXXXX-YYYYMMDD  (e.g. LMT-A3F9K2-20260306)
───────────────────────────────────────────── */
function generateAgentUID() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let uid = '';
  for (let i = 0; i < 4; i++) {
    uid += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  const now = new Date();
  const timestamp = now.getTime().toString().slice(-4);
  const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
  return `LMT-${uid}${timestamp}-${dateStr}`;
}

/* ─────────────────────────────────────────────
   OTP box component
───────────────────────────────────────────── */
function OtpBox({ index, value, onInput, inputRefs }) {
  return (
    <input
      ref={(el) => (inputRefs.current[index] = el)}
      type="text"
      inputMode="numeric"
      maxLength={1}
      className="otp-box"
      value={value}
      onChange={(e) => onInput(e, index)}
      onKeyDown={(e) => {
        if (e.key === 'Backspace' && !value && index > 0) {
          inputRefs.current[index - 1]?.focus();
        }
      }}
      onFocus={(e) => e.target.select()}
    />
  );
}

/* ─────────────────────────────────────────────
   TOTAL STEPS:
   1 → Business Info (Agency Name, Full Name, Mobile, Email)
   1.5 → Phone OTP verification (sub-step inside step 1)
   2 → NID Upload
   3 → Face Photo
   4 → Confirmation
───────────────────────────────────────────── */
function RegistrationScreen() {
  // ── Step state ───────────────────────────
  const [step, setStep] = useState(1);          // 1,2,3,4
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [showOtp, setShowOtp] = useState(false);

  // ── Form fields ──────────────────────────
  const [agencyName, setAgencyName] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [referenceCode, setReferenceCode] = useState('');
  const [agentUID] = useState(() => generateAgentUID());
  const [nidFront, setNidFront] = useState(null);
  const [nidBack, setNidBack] = useState(null);
  const [facePhoto, setFacePhoto] = useState(null);

  // ── Submission state ─────────────────────
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ── OTP state ────────────────────────────
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [mockCode] = useState(() =>
    Math.floor(100000 + Math.random() * 900000).toString()
  );
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const inputRefs = useRef([]);
  const timerRef = useRef(null);

  // ── Step 1 validation errors ─────────────
  const [step1Errors, setStep1Errors] = useState({});

  // ── File preview URLs ────────────────────
  const [nidFrontPreview, setNidFrontPreview] = useState(null);
  const [nidBackPreview, setNidBackPreview] = useState(null);
  const [facePreview, setFacePreview] = useState(null);

  /* ── Stepper progress ── */
  const totalSteps = 3;
  const stepProgress = step === 4 ? 100 : Math.round(((step - 1) / totalSteps) * 100);

  /* ────────────────────────────────────────
     OTP Timer
  ──────────────────────────────────────── */
  const startCountdown = () => {
    setCountdown(60);
    setCanResend(false);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(timerRef.current);
          setCanResend(true);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };
  useEffect(() => () => clearInterval(timerRef.current), []);

  /* ────────────────────────────────────────
     Step 1 → Send OTP
  ──────────────────────────────────────── */
  const handleSendOtp = () => {
    const errs = {};
    if (!agencyName.trim()) errs.agencyName = 'Agency name is required.';
    if (!fullName.trim()) errs.fullName = 'Full name is required.';
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) errs.email = 'A valid email is required.';
    if (!phone || !isValidPhoneNumber(phone)) errs.phone = 'Enter a valid phone number.';

    if (Object.keys(errs).length) { setStep1Errors(errs); return; }
    setStep1Errors({});
    setPhoneError('');
    setShowOtp(true);
    startCountdown();
    // Production: trigger Firebase phone auth / SMS here
    console.log(`[DEV] OTP for ${phone}: ${mockCode}`);
  };

  /* ────────────────────────────────────────
     OTP input handler
  ──────────────────────────────────────── */
  const handleOtpInput = (e, index) => {
    const val = e.target.value.replace(/\D/g, '');
    const updated = [...otp];
    updated[index] = val.slice(-1);
    setOtp(updated);
    setOtpError('');
    if (val && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  /* ────────────────────────────────────────
     Verify OTP → proceed to Step 2
  ──────────────────────────────────────── */
  const handleVerifyOtp = () => {
    const entered = otp.join('');
    if (entered.length < 6) { setOtpError('Please enter all 6 digits.'); return; }
    if (entered !== mockCode) {
      setOtpError('Incorrect code. Please try again.');
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      return;
    }
    setPhoneVerified(true);
    setShowOtp(false);
    setStep(2);
  };

  const handleResendOtp = () => {
    if (!canResend) return;
    setOtp(['', '', '', '', '', '']);
    setOtpError('');
    startCountdown();
    console.log(`[DEV] Resend OTP for ${phone}: ${mockCode}`);
  };

  /* ────────────────────────────────────────
     File pickers
  ──────────────────────────────────────── */
  const pickFile = (setter, previewSetter) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setter(file);
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      previewSetter(url);
    }
  };

  /* ────────────────────────────────────────
     Submit registration (Step 3)
  ──────────────────────────────────────── */
  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      // 1. Upload files to Firebase Storage
      const uploadFile = async (file, path) => {
        if (!file) return null;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file);
        return await getDownloadURL(storageRef);
      };

      const nidFrontUrl = await uploadFile(nidFront, `agent_documents/${agentUID}/nid_front`);
      const nidBackUrl = await uploadFile(nidBack, `agent_documents/${agentUID}/nid_back`);
      const facePhotoUrl = await uploadFile(facePhoto, `agent_documents/${agentUID}/selfie`);

      // 2. Write to Firestore
      const agentPayload = {
        agentUID,
        agencyName: agencyName.trim(),
        fullName: fullName.trim(),
        phone,
        email: email.trim(),
        referenceCode: referenceCode.trim(),
        status: 'Pending Review',
        documents: {
          nidFront: nidFrontUrl,
          nidBack: nidBackUrl,
          selfie: facePhotoUrl
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await addDoc(collection(db, 'users'), agentPayload);

      console.log('Successfully saved to Firestore');
      setStep(4);
    } catch (err) {
      console.error('Submission error:', err);
      setError('Registration failed: ' + (err.message || 'Please try again.'));
    }
    setLoading(false);
  };

  /* ═══════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════ */
  return (
    <div className="reg-shell">
      {/* ── Side Panel (desktop only) ─────── */}
      <aside className="reg-side">
        <div className="reg-side-logo">
          <span className="material-symbols-outlined">location_on</span>
        </div>
        <h1 className="reg-side-title">Lymet Agency</h1>
        <p className="reg-side-sub">Join our trusted network of verified agencies worldwide.</p>

        <div className="reg-side-steps">
          {[
            { n: 1, icon: 'business', label: 'Business Info' },
            { n: 2, icon: 'badge', label: 'NID Verification' },
            { n: 3, icon: 'face', label: 'Face Scan' },
          ].map(({ n, icon, label }) => (
            <div key={n} className={`reg-side-step ${step >= n ? 'active' : ''} ${step > n ? 'done' : ''}`}>
              <div className="reg-side-step-icon">
                {step > n
                  ? <span className="material-symbols-outlined">check</span>
                  : <span className="material-symbols-outlined">{icon}</span>
                }
              </div>
              <span>{label}</span>
            </div>
          ))}
        </div>

        <div className="reg-side-uid">
          <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>fingerprint</span>
          <span>Agent UID: <strong>{agentUID}</strong></span>
        </div>
      </aside>

      {/* ── Main Card ────────────────────── */}
      <main className="reg-main">
        <div className="reg-card">

          {/* Progress bar */}
          {step < 4 && (
            <div className="reg-progress-wrap">
              <div className="reg-progress-bar" style={{ width: `${stepProgress}%` }} />
            </div>
          )}

          {/* ══════════════════════════════
              STEP 1 — BUSINESS INFO FORM
          ══════════════════════════════ */}
          {step === 1 && !showOtp && (
            <div className="reg-step-content">
              <div className="reg-step-header">
                <div className="reg-step-icon-circle">
                  <span className="material-symbols-outlined">business</span>
                </div>
                <h2 className="reg-step-title">Business Info</h2>
                <p className="reg-step-desc">Provide your agency details to begin registration.</p>
              </div>

              {/* Agent UID (read-only, auto-generated) */}
              <div className="reg-field-group">
                <label className="reg-label">
                  <span className="material-symbols-outlined reg-label-icon">fingerprint</span>
                  Agent UID
                  <span className="reg-badge-auto">Auto-generated</span>
                </label>
                <div className="reg-uid-box">
                  <span className="material-symbols-outlined reg-uid-icon">tag</span>
                  <span className="reg-uid-value">{agentUID}</span>
                  <span className="reg-uid-lock material-symbols-outlined">lock</span>
                </div>
                <p className="reg-field-hint">This unique ID is permanently assigned to your agency application.</p>
              </div>

              {/* Agency Name */}
              <div className="reg-field-group">
                <label className="reg-label" htmlFor="agencyName">
                  <span className="material-symbols-outlined reg-label-icon">apartment</span>
                  Agency Name
                </label>
                <input
                  id="agencyName"
                  className={`reg-input ${step1Errors.agencyName ? 'reg-input-error' : ''}`}
                  type="text"
                  placeholder="e.g. Stellar Travel Agency"
                  value={agencyName}
                  onChange={(e) => { setAgencyName(e.target.value); setStep1Errors(p => ({ ...p, agencyName: '' })); }}
                />
                {step1Errors.agencyName && <p className="reg-error-text">{step1Errors.agencyName}</p>}
              </div>

              {/* Your Full Name */}
              <div className="reg-field-group">
                <label className="reg-label" htmlFor="fullName">
                  <span className="material-symbols-outlined reg-label-icon">person</span>
                  Your Full Name
                </label>
                <input
                  id="fullName"
                  className={`reg-input ${step1Errors.fullName ? 'reg-input-error' : ''}`}
                  type="text"
                  placeholder="e.g. Mohammad Rafiq Hossain"
                  value={fullName}
                  onChange={(e) => { setFullName(e.target.value); setStep1Errors(p => ({ ...p, fullName: '' })); }}
                />
                {step1Errors.fullName && <p className="reg-error-text">{step1Errors.fullName}</p>}
              </div>

              {/* Mobile Number (country picker) */}
              <div className="reg-field-group">
                <label className="reg-label">
                  <span className="material-symbols-outlined reg-label-icon">smartphone</span>
                  Mobile Number
                  {phoneVerified && (
                    <span className="reg-badge-verified">
                      <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>verified</span>
                      Verified
                    </span>
                  )}
                </label>
                <div className={`reg-phone-wrap ${step1Errors.phone ? 'reg-phone-error' : ''} ${phoneVerified ? 'reg-phone-verified' : ''}`}>
                  <PhoneInput
                    international
                    countryCallingCodeEditable={false}
                    defaultCountry="BD"
                    value={phone}
                    onChange={(val) => {
                      setPhone(val || '');
                      setPhoneVerified(false);
                      setStep1Errors(p => ({ ...p, phone: '' }));
                    }}
                    disabled={phoneVerified}
                    placeholder="Enter phone number"
                    className="reg-phone-input"
                  />
                </div>
                {step1Errors.phone && <p className="reg-error-text">{step1Errors.phone}</p>}
                {!phoneVerified && (
                  <p className="reg-field-hint">
                    <span className="material-symbols-outlined" style={{ fontSize: '13px', verticalAlign: 'middle' }}>globe</span>
                    {' '}Tap the flag to change country
                  </p>
                )}
              </div>

              {/* Email */}
              <div className="reg-field-group">
                <label className="reg-label" htmlFor="email">
                  <span className="material-symbols-outlined reg-label-icon">mail</span>
                  Email Address
                </label>
                <input
                  id="email"
                  className={`reg-input ${step1Errors.email ? 'reg-input-error' : ''}`}
                  type="email"
                  placeholder="agency@example.com"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setStep1Errors(p => ({ ...p, email: '' })); }}
                />
                {step1Errors.email && <p className="reg-error-text">{step1Errors.email}</p>}
              </div>

              {/* Reference Code (optional) */}
              <div className="reg-field-group">
                <label className="reg-label" htmlFor="referenceCode">
                  <span className="material-symbols-outlined reg-label-icon">confirmation_number</span>
                  Reference Code
                  <span className="reg-badge-optional">Optional</span>
                </label>
                <input
                  id="referenceCode"
                  className="reg-input"
                  type="text"
                  placeholder="e.g. REF-2024-ALPHA"
                  value={referenceCode}
                  onChange={(e) => setReferenceCode(e.target.value)}
                />
                <p className="reg-field-hint">Enter a referral or partner code if you have one.</p>
              </div>

              <button className="reg-primary-btn" type="button" onClick={handleSendOtp}>
                <span>Send Verification Code</span>
                <span className="material-symbols-outlined">send</span>
              </button>
            </div>
          )}

          {/* ══════════════════════════════
              STEP 1 (OTP sub-step) — VERIFY PHONE
          ══════════════════════════════ */}
          {step === 1 && showOtp && (
            <div className="reg-step-content">
              <button
                className="reg-back-btn"
                type="button"
                onClick={() => { setShowOtp(false); setOtp(['', '', '', '', '', '']); setOtpError(''); }}
              >
                <span className="material-symbols-outlined">arrow_back</span>
                Back
              </button>

              <div className="reg-step-header">
                <div className="reg-step-icon-circle reg-icon-purple">
                  <span className="material-symbols-outlined">mark_email_read</span>
                </div>
                <h2 className="reg-step-title">Verify Your Number</h2>
                <p className="reg-step-desc">
                  We sent a 6-digit code to <strong>{phone}</strong>
                </p>
                <p className="reg-dev-hint">
                  <span className="material-symbols-outlined" style={{ fontSize: '13px', verticalAlign: 'middle' }}>developer_mode</span>
                  {' '}Dev mode — check your browser console for the code.
                </p>
              </div>

              <div className="reg-otp-row" onPaste={handlePaste}>
                {otp.map((digit, i) => (
                  <OtpBox key={i} index={i} value={digit} onInput={handleOtpInput} inputRefs={inputRefs} />
                ))}
              </div>

              {otpError && <p className="reg-error-text reg-error-center">{otpError}</p>}

              <button
                className="reg-primary-btn"
                type="button"
                onClick={handleVerifyOtp}
                disabled={otp.join('').length < 6}
              >
                <span className="material-symbols-outlined">verified</span>
                <span>Verify Code</span>
              </button>

              <div className="reg-resend-row">
                {canResend ? (
                  <button className="reg-resend-btn" type="button" onClick={handleResendOtp}>
                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>refresh</span>
                    Resend Code
                  </button>
                ) : (
                  <p className="reg-countdown">
                    Resend in <strong>0:{countdown.toString().padStart(2, '0')}</strong>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ══════════════════════════════
              STEP 2 — NID UPLOAD
          ══════════════════════════════ */}
          {step === 2 && (
            <div className="reg-step-content">
              <button className="reg-back-btn" type="button" onClick={() => setStep(1)}>
                <span className="material-symbols-outlined">arrow_back</span>
                Back
              </button>

              <div className="reg-step-header">
                <div className="reg-step-icon-circle reg-icon-amber">
                  <span className="material-symbols-outlined">badge</span>
                </div>
                <h2 className="reg-step-title">NID Verification</h2>
                <p className="reg-step-desc">Upload clear photos of both sides of your National ID card.</p>
              </div>

              {/* Front */}
              <div className="reg-field-group">
                <label className="reg-label">
                  <span className="material-symbols-outlined reg-label-icon">badge</span>
                  Front Side of NID
                </label>
                <label className={`reg-upload-card ${nidFront ? 'reg-upload-done' : ''}`} htmlFor="nidFront">
                  {nidFrontPreview
                    ? <img src={nidFrontPreview} alt="NID front" className="reg-upload-preview" />
                    : (
                      <>
                        <span className="material-symbols-outlined reg-upload-icon">photo_camera</span>
                        <span className="reg-upload-label">Tap to upload front side</span>
                        <span className="reg-upload-sub">JPG, PNG or PDF</span>
                      </>
                    )
                  }
                  {nidFront && !nidFrontPreview && (
                    <span className="reg-upload-filename">{nidFront.name}</span>
                  )}
                </label>
                <input id="nidFront" type="file" accept="image/*,.pdf" className="reg-hidden-input"
                  onChange={pickFile(setNidFront, setNidFrontPreview)} />
              </div>

              {/* Back */}
              <div className="reg-field-group">
                <label className="reg-label">
                  <span className="material-symbols-outlined reg-label-icon">credit_card</span>
                  Back Side of NID
                </label>
                <label className={`reg-upload-card ${nidBack ? 'reg-upload-done' : ''}`} htmlFor="nidBack">
                  {nidBackPreview
                    ? <img src={nidBackPreview} alt="NID back" className="reg-upload-preview" />
                    : (
                      <>
                        <span className="material-symbols-outlined reg-upload-icon">flip_to_back</span>
                        <span className="reg-upload-label">Tap to upload back side</span>
                        <span className="reg-upload-sub">JPG, PNG or PDF</span>
                      </>
                    )
                  }
                  {nidBack && !nidBackPreview && (
                    <span className="reg-upload-filename">{nidBack.name}</span>
                  )}
                </label>
                <input id="nidBack" type="file" accept="image/*,.pdf" className="reg-hidden-input"
                  onChange={pickFile(setNidBack, setNidBackPreview)} />
              </div>

              <div className="reg-privacy-badge">
                <span className="material-symbols-outlined" style={{ fontSize: '15px' }}>lock</span>
                Your documents are encrypted and privacy protected
              </div>

              <button
                className="reg-primary-btn"
                type="button"
                onClick={() => setStep(3)}
                disabled={!nidFront || !nidBack}
              >
                <span>Continue</span>
                <span className="material-symbols-outlined">arrow_forward</span>
              </button>
            </div>
          )}

          {/* ══════════════════════════════
              STEP 3 — FACE PHOTO
          ══════════════════════════════ */}
          {step === 3 && (
            <div className="reg-step-content">
              <button className="reg-back-btn" type="button" onClick={() => setStep(2)}>
                <span className="material-symbols-outlined">arrow_back</span>
                Back
              </button>

              <div className="reg-step-header">
                <div className="reg-step-icon-circle reg-icon-green">
                  <span className="material-symbols-outlined">face</span>
                </div>
                <h2 className="reg-step-title">Liveness Check</h2>
                <p className="reg-step-desc">Take a clear selfie or upload a recent photo of yourself.</p>
              </div>

              <label className={`reg-face-upload ${facePhoto ? 'reg-upload-done' : ''}`} htmlFor="facePhoto">
                {facePreview
                  ? <img src={facePreview} alt="Face" className="reg-face-preview" />
                  : (
                    <>
                      <span className="material-symbols-outlined reg-face-icon">person</span>
                      <span className="reg-upload-label">Take or upload a selfie</span>
                      <span className="reg-upload-sub">Front-facing, well-lit photo</span>
                    </>
                  )
                }
              </label>
              <input id="facePhoto" type="file" accept="image/*" capture="user"
                className="reg-hidden-input"
                onChange={pickFile(setFacePhoto, setFacePreview)} />

              {/* Summary card before submit */}
              <div className="reg-summary-card">
                <h3 className="reg-summary-title">Registration Summary</h3>
                <div className="reg-summary-row">
                  <span className="reg-summary-key">Agent UID</span>
                  <span className="reg-summary-val reg-uid-pill">{agentUID}</span>
                </div>
                <div className="reg-summary-row">
                  <span className="reg-summary-key">Agency</span>
                  <span className="reg-summary-val">{agencyName}</span>
                </div>
                <div className="reg-summary-row">
                  <span className="reg-summary-key">Full Name</span>
                  <span className="reg-summary-val">{fullName}</span>
                </div>
                <div className="reg-summary-row">
                  <span className="reg-summary-key">Mobile</span>
                  <span className="reg-summary-val">
                    {phone}
                    <span className="material-symbols-outlined" style={{ fontSize: '14px', color: '#16a34a', marginLeft: '4px', verticalAlign: 'middle' }}>verified</span>
                  </span>
                </div>
                <div className="reg-summary-row">
                  <span className="reg-summary-key">Email</span>
                  <span className="reg-summary-val">{email}</span>
                </div>
                {referenceCode && (
                  <div className="reg-summary-row">
                    <span className="reg-summary-key">Ref. Code</span>
                    <span className="reg-summary-val">{referenceCode}</span>
                  </div>
                )}
              </div>

              {error && <p className="reg-error-text reg-error-center">{error}</p>}

              <button
                className="reg-primary-btn reg-submit-btn"
                type="button"
                disabled={!facePhoto || loading}
                onClick={handleSubmit}
              >
                {loading
                  ? <><span className="reg-spinner" /><span>Submitting…</span></>
                  : <><span className="material-symbols-outlined">send</span><span>Submit Application</span></>
                }
              </button>
            </div>
          )}

          {/* ══════════════════════════════
              STEP 4 — SUCCESS
          ══════════════════════════════ */}
          {step === 4 && (
            <div className="reg-step-content reg-success-content">
              <div className="reg-success-icon">
                <span className="material-symbols-outlined">task_alt</span>
              </div>
              <h2 className="reg-success-title">Application Submitted!</h2>
              <p className="reg-success-desc">
                Your agency registration has been received. Our team will review it and respond within 2–3 business days.
              </p>

              <div className="reg-uid-confirm">
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>fingerprint</span>
                <span>Your Agent UID:</span>
                <strong>{agentUID}</strong>
              </div>

              <p className="reg-success-hint">
                Save your Agent UID for tracking your application status in the admin portal.
              </p>

              <button className="reg-primary-btn" type="button" onClick={() => window.location.reload()}>
                <span className="material-symbols-outlined">add_circle</span>
                <span>Register Another Agency</span>
              </button>
            </div>
          )}

        </div>
      </main>
    </div>
  );
}

export default RegistrationScreen;
